import {
  createClientId,
  CURTAIN_QUOTE_TYPE,
  GENERAL_QUOTE_TYPE,
} from "./generalQuotationUtils";

export const CRM_PIPELINE_TYPES = [CURTAIN_QUOTE_TYPE, GENERAL_QUOTE_TYPE];
export const CRM_CHECKLIST_MAX_DEPTH = 3;
export const CRM_REMINDER_TYPE_TASK_DUE = "task_due";
export const CRM_REMINDER_TYPE_INSTALLATION = "installation";
export const CRM_REMINDER_STATUS_ACTIVE = "active";
export const CRM_REMINDER_STATUS_DONE = "done";
export const CRM_REMINDER_STATUS_DISABLED = "disabled";
export const CRM_FOLLOW_UP_STATUS_PENDING = "pending";
export const CRM_FOLLOW_UP_STATUS_DONE = "done";
export const CRM_FOLLOW_UP_PRIORITY_LOW = "low";
export const CRM_FOLLOW_UP_PRIORITY_MEDIUM = "medium";
export const CRM_FOLLOW_UP_PRIORITY_HIGH = "high";
export const DEFAULT_CRM_REMINDER_TIME = "09:00";
export const CRM_REMINDER_REPEAT_OPTIONS = [
  { key: "10m", label: "Every 10 minutes", minutes: 10 },
  { key: "30m", label: "Every 30 minutes", minutes: 30 },
  { key: "1h", label: "Every 1 hour", minutes: 60 },
  { key: "daily", label: "Every day", minutes: 1440 },
];
export const CRM_FOLLOW_UP_REPEAT_OPTIONS = [
  { key: "none", label: "No repeat", minutes: 0 },
  { key: "10m", label: "Every 10 minutes", minutes: 10 },
  { key: "30m", label: "Every 30 minutes", minutes: 30 },
  { key: "1h", label: "Every 1 hour", minutes: 60 },
  { key: "daily", label: "Every day", minutes: 1440 },
  { key: "weekly", label: "Every week", minutes: 10080 },
];
export const CRM_FOLLOW_UP_PRIORITY_OPTIONS = [
  { key: CRM_FOLLOW_UP_PRIORITY_LOW, label: "Low" },
  { key: CRM_FOLLOW_UP_PRIORITY_MEDIUM, label: "Medium" },
  { key: CRM_FOLLOW_UP_PRIORITY_HIGH, label: "High" },
];

const normalizeExecutionSpecAttributes = (attributes = {}) => {
  if (!attributes || typeof attributes !== "object" || Array.isArray(attributes)) {
    return {};
  }

  return Object.entries(attributes).reduce((accumulator, [key, value]) => {
    const nextKey = String(key || "").trim();
    if (!nextKey) return accumulator;

    accumulator[nextKey] =
      value == null
        ? ""
        : typeof value === "string"
        ? value
        : String(value);
    return accumulator;
  }, {});
};

const normalizeExecutionSpecAttachment = (attachment = {}) => ({
  id:
    String(attachment?.id || "").trim() ||
    createClientId("exec-attachment"),
  name:
    String(attachment?.name || attachment?.filename || "").trim() ||
    "Attachment",
  filename: String(attachment?.filename || attachment?.name || "attachment").trim(),
  url: String(attachment?.url || attachment?.downloadURL || attachment?.downloadUrl || "").trim(),
  downloadURL: String(attachment?.downloadURL || attachment?.downloadUrl || attachment?.url || "").trim(),
  thumbnailDownloadURL: String(
    attachment?.thumbnailDownloadURL ||
      attachment?.thumbnailUrl ||
      attachment?.thumbnailURL ||
      ""
  ).trim(),
  thumbnailFullPath: String(
    attachment?.thumbnailFullPath || attachment?.thumbnailStoragePath || ""
  ).trim(),
  path: String(attachment?.path || attachment?.storagePath || "").trim(),
  storagePath: String(attachment?.storagePath || attachment?.path || "").trim(),
  fullPath: String(attachment?.fullPath || attachment?.path || attachment?.storagePath || "").trim(),
  fileId: String(attachment?.fileId || "").trim(),
  imageId: String(attachment?.imageId || attachment?.id || "").trim(),
  contentType: String(attachment?.contentType || "").trim(),
  sizeBytes: Number(attachment?.sizeBytes || 0),
  thumbnailSizeBytes: Number(attachment?.thumbnailSizeBytes || 0),
  originalSizeBytes: Number(attachment?.originalSizeBytes || 0),
  optimizedSizeBytes: Number(attachment?.optimizedSizeBytes || 0),
  compressionRatio: Number(attachment?.compressionRatio || 0),
  width: Number(attachment?.width || 0),
  height: Number(attachment?.height || 0),
  originalWidth: Number(attachment?.originalWidth || 0),
  originalHeight: Number(attachment?.originalHeight || 0),
  uploadStatus: String(attachment?.uploadStatus || "").trim(),
  compressionStatus: String(attachment?.compressionStatus || "").trim(),
  thumbnailStatus: String(attachment?.thumbnailStatus || "").trim(),
  ownerType: String(attachment?.ownerType || "").trim(),
  workspaceUid: String(attachment?.workspaceUid || "").trim(),
  leadId: String(attachment?.leadId || "").trim(),
  ownerId: String(attachment?.ownerId || "").trim(),
  removedFromRecord: attachment?.removedFromRecord === true,
  archived: attachment?.archived === true,
  uploadedAt: attachment?.uploadedAt || null,
  uploadedBy: String(attachment?.uploadedBy || attachment?.uploadedByUid || "").trim(),
  uploadedByName: String(attachment?.uploadedByName || "").trim(),
});

export const normalizeExecutionSpecAttachments = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .filter(Boolean)
    .map((attachment) => normalizeExecutionSpecAttachment(attachment));

export const normalizeExecutionTemplateItemType = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const toExecutionTemplateDraftString = (value = "") =>
  value == null ? "" : String(value);

export const createExecutionTemplateFieldDraft = (
  key = "",
  defaultValue = ""
) => ({
  id: createClientId("exec-template-field"),
  key: toExecutionTemplateDraftString(key),
  defaultValue: toExecutionTemplateDraftString(defaultValue),
});

const normalizeExecutionTemplateFieldDraft = (field = {}) => {
  if (typeof field === "string") {
    return createExecutionTemplateFieldDraft(field, "");
  }

  return {
    id:
      String(field?.id || "").trim() ||
      createClientId("exec-template-field"),
    key: toExecutionTemplateDraftString(
      field?.key ?? field?.name ?? field?.label ?? ""
    ),
    defaultValue:
      field?.defaultValue == null
        ? field?.value == null
          ? ""
          : String(field.value)
        : String(field.defaultValue),
  };
};

const normalizeExecutionTemplateField = (field = {}) => {
  const draftField = normalizeExecutionTemplateFieldDraft(field);
  return {
    ...draftField,
    key: String(draftField?.key || "").trim(),
  };
};

export const createExecutionSpecItem = (overrides = {}) => ({
  id: String(overrides?.id || "").trim() || createClientId("exec-item"),
  itemType: String(overrides?.itemType || "").trim(),
  itemName: String(overrides?.itemName || "").trim(),
  attributes: normalizeExecutionSpecAttributes(overrides?.attributes),
  attachments: normalizeExecutionSpecAttachments(overrides?.attachments),
  createdAt: overrides?.createdAt || null,
  updatedAt: overrides?.updatedAt || null,
});

export const normalizeExecutionSpecs = (items = []) =>
  (Array.isArray(items) ? items : [])
    .filter(Boolean)
    .map((item) => createExecutionSpecItem(item));

export const createExecutionTemplateDraft = (
  name = "",
  itemType = "",
  fields = []
) => ({
  id: createClientId("exec-template"),
  name: toExecutionTemplateDraftString(name),
  itemType: toExecutionTemplateDraftString(itemType || name),
  fields: (Array.isArray(fields) ? fields : [])
    .map((field) => normalizeExecutionTemplateFieldDraft(field)),
  createdAt: null,
  updatedAt: null,
});

export const normalizeExecutionTemplateDrafts = (templates = []) =>
  (Array.isArray(templates) ? templates : [])
    .filter(Boolean)
    .map((template) => ({
      id: String(template?.id || "").trim() || createClientId("exec-template"),
      name: toExecutionTemplateDraftString(template?.name ?? ""),
      itemType: toExecutionTemplateDraftString(
        template?.itemType ?? template?.name ?? ""
      ),
      fields: (Array.isArray(template?.fields) ? template.fields : [])
        .map((field) => normalizeExecutionTemplateFieldDraft(field)),
      createdAt: template?.createdAt || null,
      updatedAt: template?.updatedAt || null,
    }));

export const normalizeExecutionTemplates = (templates = []) =>
  normalizeExecutionTemplateDrafts(templates).map((template) => ({
    ...template,
    name: String(template?.name || "").trim(),
    itemType: normalizeExecutionTemplateItemType(
      template?.itemType || template?.name || ""
    ),
    fields: (Array.isArray(template?.fields) ? template.fields : [])
      .map((field) => normalizeExecutionTemplateField(field))
      .filter((field) => String(field?.key || "").trim()),
  }));

const createChecklistNode = (
  label = "",
  prefix = "crm-check",
  overrides = {}
) => {
  const completed = getChecklistCompletionValue(overrides);

  return {
    id: overrides.id || createClientId(prefix),
    templateId: overrides.templateId || "",
    label: String(label ?? ""),
    title: String(overrides.title ?? label ?? ""),
    done: completed,
    completed,
    checked: completed,
    assignedToUserId: String(overrides.assignedToUserId || ""),
    assignedToNameSnapshot: String(
      overrides.assignedToNameSnapshot || overrides.assignedTo || ""
    ),
    assignedAt: overrides.assignedAt || null,
    createdAt: overrides.createdAt || null,
    createdBy: overrides.createdBy || null,
    updatedAt: overrides.updatedAt || null,
    completedAt: completed ? overrides.completedAt || null : null,
    completedBy: completed ? overrides.completedBy || null : null,
    notes: String(overrides.notes || ""),
    children: Array.isArray(overrides.children) ? overrides.children : [],
  };
};

const toDateInputString = (value) => {
  if (!value) return "";

  if (typeof value?.toDate === "function") {
    return toDateInputString(value.toDate());
  }

  const date =
    value instanceof Date
      ? new Date(value.getTime())
      : typeof value === "number"
      ? new Date(value)
      : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeReminderRepeatKey = (value) => {
  const nextValue = String(value || "").trim();
  return CRM_REMINDER_REPEAT_OPTIONS.some((option) => option.key === nextValue)
    ? nextValue
    : "daily";
};

const normalizeReminderTime = (value) => {
  const nextValue = String(value || "").trim();
  return /^\d{2}:\d{2}$/.test(nextValue)
    ? nextValue
    : DEFAULT_CRM_REMINDER_TIME;
};

const normalizeReminderStatus = (status, enabled) => {
  if (status === CRM_REMINDER_STATUS_DONE) {
    return CRM_REMINDER_STATUS_DONE;
  }

  return enabled ? CRM_REMINDER_STATUS_ACTIVE : CRM_REMINDER_STATUS_DISABLED;
};

const normalizeBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }
  return undefined;
};

const getChecklistCompletionValue = (item = {}) =>
  normalizeBoolean(item?.completed) === true ||
  normalizeBoolean(item?.done) === true ||
  normalizeBoolean(item?.checked) === true ||
  normalizeBoolean(item?.isComplete) === true;

const getChecklistChildren = (item) => {
  if (Array.isArray(item?.children)) return item.children;
  if (Array.isArray(item?.subItems)) return item.subItems;
  if (Array.isArray(item?.subChecklist)) return item.subChecklist;
  return [];
};

const normalizeReminderConfigEntry = (type, config = {}, fallbackDate = "") => {
  const reminderDate = String(config?.reminderDate ?? fallbackDate ?? "");
  const reminderEnabledValue = normalizeBoolean(config?.reminderEnabled);
  const enabledValue = normalizeBoolean(config?.enabled);
  const enabled =
    reminderEnabledValue !== undefined
      ? reminderEnabledValue
      : enabledValue !== undefined
      ? enabledValue
      : Boolean(reminderDate);

  return {
    type,
    enabled,
    reminderEnabled: enabled,
    reminderDate,
    reminderTime: normalizeReminderTime(config?.reminderTime),
    repeatKey: normalizeReminderRepeatKey(config?.repeatKey),
    status: normalizeReminderStatus(config?.status, enabled),
    completedAt: config?.completedAt || null,
  };
};

const createStage = ({
  id,
  label,
  color,
  confirmsQuote = false,
  ordersMaterial = false,
  marksComplete = false,
  isCancelled = false,
  defaultChecklist = [],
}) => ({
  id,
  label,
  color,
  confirmsQuote,
  ordersMaterial,
  marksComplete,
  isCancelled,
  defaultChecklist,
});

const DEFAULT_CURTAIN_STAGES = [
  createStage({
    id: "quote_given",
    label: "Quote Given",
    color: "#2563eb",
    defaultChecklist: [
      { label: "Site measurements verified" },
      { label: "Client approved quote" },
    ],
  }),
  createStage({
    id: "measurement_pending",
    label: "Measurement Pending",
    color: "#38bdf8",
    defaultChecklist: [
      { label: "Schedule site visit" },
      { label: "Collect final dimensions" },
    ],
  }),
  createStage({
    id: "follow_up",
    label: "Follow-up",
    color: "#8b5cf6",
    defaultChecklist: [
      { label: "Share revisions with client" },
      { label: "Confirm decision timeline" },
    ],
  }),
  createStage({
    id: "advance_received",
    label: "Advance Received",
    color: "#6366f1",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Advance payment recorded" },
      { label: "Final fabric selection approved" },
    ],
  }),
  createStage({
    id: "materials_ordered",
    label: "Material Ordered",
    color: "#f59e0b",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Fabric ordered" },
      { label: "Blinds / accessories ordered" },
      { label: "Vendor ETA confirmed" },
    ],
  }),
  createStage({
    id: "stitching_started",
    label: "Stitching Started",
    color: "#a855f7",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Fabric received" },
      { label: "Stitching in progress" },
    ],
  }),
  createStage({
    id: "ready_for_installation",
    label: "Ready For Installation",
    color: "#10b981",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Final QC completed" },
      { label: "Installation team scheduled" },
    ],
  }),
  createStage({
    id: "installed",
    label: "Installed",
    color: "#059669",
    confirmsQuote: true,
    marksComplete: true,
    defaultChecklist: [
      { label: "Site installation completed" },
      { label: "Client handover done" },
    ],
  }),
  createStage({
    id: "cancelled",
    label: "Cancelled",
    color: "#ef4444",
    isCancelled: true,
    defaultChecklist: [],
  }),
];

const DEFAULT_GENERAL_STAGES = [
  createStage({
    id: "quote_given",
    label: "Quotation Sent",
    color: "#2563eb",
    defaultChecklist: [
      { label: "Scope shared with client" },
      { label: "Quotation mailed to client" },
    ],
  }),
  createStage({
    id: "design_discussion",
    label: "Design Discussion",
    color: "#0ea5e9",
    defaultChecklist: [
      { label: "Reference images collected" },
      { label: "Material options presented" },
    ],
  }),
  createStage({
    id: "negotiation",
    label: "Negotiation",
    color: "#8b5cf6",
    defaultChecklist: [
      { label: "Commercial revisions shared" },
      { label: "Final approval pending" },
    ],
  }),
  createStage({
    id: "advance_received",
    label: "Advance Received",
    color: "#6366f1",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Advance payment recorded" },
      { label: "Production brief approved" },
    ],
  }),
  createStage({
    id: "in_production",
    label: "In Production",
    color: "#f59e0b",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "Production plan released" },
      { label: "Materials procured" },
    ],
  }),
  createStage({
    id: "ready_for_dispatch",
    label: "Ready For Dispatch",
    color: "#10b981",
    confirmsQuote: true,
    defaultChecklist: [
      { label: "QC completed" },
      { label: "Dispatch/installation scheduled" },
    ],
  }),
  createStage({
    id: "installed",
    label: "Delivered / Installed",
    color: "#059669",
    confirmsQuote: true,
    marksComplete: true,
    defaultChecklist: [
      { label: "Delivery or installation completed" },
      { label: "Client sign-off received" },
    ],
  }),
  createStage({
    id: "cancelled",
    label: "Cancelled",
    color: "#ef4444",
    isCancelled: true,
    defaultChecklist: [],
  }),
];

export const DEFAULT_CRM_PIPELINES = {
  [CURTAIN_QUOTE_TYPE]: {
    key: CURTAIN_QUOTE_TYPE,
    label: "Curtains Pipeline",
    stages: DEFAULT_CURTAIN_STAGES,
  },
  [GENERAL_QUOTE_TYPE]: {
    key: GENERAL_QUOTE_TYPE,
    label: "General Quotation Pipeline",
    stages: DEFAULT_GENERAL_STAGES,
  },
};

export const getQuoteTypeLabel = (quoteType) =>
  quoteType === GENERAL_QUOTE_TYPE ? "General Quotation" : "Curtains";

export const createDealChecklistItem = (label = "", overrides = {}) =>
  createChecklistNode(label, "crm-check", overrides);

const normalizeChecklistNode = (
  item,
  depth = 1,
  { templateMode = false, idPrefix } = {}
) => {
  if (!item || depth > CRM_CHECKLIST_MAX_DEPTH) return null;

  if (typeof item === "string") {
    const label = String(item).trim();
    if (!label) return null;
    return createChecklistNode(label, idPrefix || "crm-stage-task", {
      children: [],
    });
  }

  const children = getChecklistChildren(item)
    .map((child) =>
      normalizeChecklistNode(child, depth + 1, {
        templateMode,
        idPrefix,
      })
    )
    .filter(Boolean);
  const label = String(item?.label ?? item?.title ?? "");
  if (!label.trim() && !item?.id && children.length === 0) return null;
  const done = templateMode ? false : getChecklistCompletionValue(item);

  return createChecklistNode(label, idPrefix || "crm-check", {
    id: item?.id,
    templateId: item?.templateId || "",
    done,
    completed: done,
    checked: done,
    assignedToUserId: templateMode ? "" : item?.assignedToUserId || "",
    assignedToNameSnapshot: templateMode
      ? ""
      : item?.assignedToNameSnapshot || item?.assignedTo || "",
    assignedAt: templateMode ? null : item?.assignedAt || null,
    createdAt: templateMode ? null : item?.createdAt || null,
    createdBy: templateMode ? null : item?.createdBy || null,
    updatedAt: templateMode ? null : item?.updatedAt || null,
    completedAt: !templateMode && done ? item?.completedAt || null : null,
    completedBy: !templateMode && done ? item?.completedBy || null : null,
    notes: item?.notes || "",
    children,
  });
};

const syncChecklistNodeCompletion = (node) => {
  const normalizedChildren = getChecklistChildren(node)
    .map((child) => syncChecklistNodeCompletion(child))
    .filter(Boolean);

  const done =
    normalizedChildren.length > 0
      ? normalizedChildren.every((child) => child.done === true)
      : getChecklistCompletionValue(node);

  return {
    ...node,
    done,
    completed: done,
    checked: done,
    title: node?.title ?? node?.label ?? "",
    completedAt: done ? node?.completedAt || null : null,
    completedBy: done ? node?.completedBy || null : null,
    children: normalizedChildren,
  };
};

export const syncChecklistTreeCompletion = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) =>
    syncChecklistNodeCompletion(item)
  );

export const normalizeChecklistTemplate = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      normalizeChecklistNode(item, 1, {
        templateMode: true,
        idPrefix: "crm-stage-task",
      })
    )
    .filter(Boolean);

export const normalizeChecklistTree = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      normalizeChecklistNode(item, 1, {
        templateMode: false,
        idPrefix: "crm-check",
      })
    )
    .filter(Boolean)
    .map((item) => syncChecklistNodeCompletion(item));

const preserveChecklistDraftNode = (
  item,
  depth = 1,
  { templateMode = false, idPrefix } = {}
) => {
  if (!item || depth > CRM_CHECKLIST_MAX_DEPTH) return null;

  if (typeof item === "string") {
    return createChecklistNode(String(item), idPrefix || "crm-check", {
      children: [],
    });
  }

  const children = getChecklistChildren(item)
    .map((child) =>
      preserveChecklistDraftNode(child, depth + 1, {
        templateMode,
        idPrefix,
      })
    )
    .filter(Boolean);

  return createChecklistNode(item?.label ?? item?.title ?? "", idPrefix || "crm-check", {
    id: item?.id,
    templateId: item?.templateId || "",
    done: templateMode ? false : getChecklistCompletionValue(item),
    completed: templateMode ? false : getChecklistCompletionValue(item),
    checked: templateMode ? false : getChecklistCompletionValue(item),
    assignedToUserId: templateMode ? "" : item?.assignedToUserId || "",
    assignedToNameSnapshot: templateMode
      ? ""
      : item?.assignedToNameSnapshot || item?.assignedTo || "",
    assignedAt: templateMode ? null : item?.assignedAt || null,
    createdAt: templateMode ? null : item?.createdAt || null,
    createdBy: templateMode ? null : item?.createdBy || null,
    updatedAt: templateMode ? null : item?.updatedAt || null,
    completedAt: templateMode ? null : item?.completedAt || null,
    completedBy: templateMode ? null : item?.completedBy || null,
    notes: item?.notes || "",
    children,
  });
};

export const preserveChecklistDraftTree = (
  items = [],
  { templateMode = false, idPrefix } = {}
) =>
  (Array.isArray(items) ? items : [])
    .map((item) =>
      preserveChecklistDraftNode(item, 1, {
        templateMode,
        idPrefix: idPrefix || (templateMode ? "crm-stage-task" : "crm-check"),
      })
    )
    .filter(Boolean)
    .map((item) => syncChecklistNodeCompletion(item));

export const cloneChecklistTree = (
  items = [],
  {
    idPrefix = "crm-check",
    preserveIds = false,
    resetState = false,
    useTemplateIds = false,
  } = {}
) =>
  (Array.isArray(items) ? items : []).map((item) => {
    const children = cloneChecklistTree(getChecklistChildren(item), {
      idPrefix,
      preserveIds,
      resetState,
      useTemplateIds,
    });

    return createChecklistNode(item?.label || item?.title || "", idPrefix, {
      id: preserveIds ? item?.id : undefined,
      templateId:
        useTemplateIds === true
          ? item?.templateId || item?.id || ""
          : item?.templateId || "",
      done: resetState ? false : getChecklistCompletionValue(item),
      completed: resetState ? false : getChecklistCompletionValue(item),
      checked: resetState ? false : getChecklistCompletionValue(item),
      assignedToUserId: resetState ? "" : item?.assignedToUserId || "",
      assignedToNameSnapshot: resetState
        ? ""
        : item?.assignedToNameSnapshot || item?.assignedTo || "",
      assignedAt: resetState ? null : item?.assignedAt || null,
      createdAt: resetState ? null : item?.createdAt || null,
      createdBy: resetState ? null : item?.createdBy || null,
      updatedAt: resetState ? null : item?.updatedAt || null,
      completedAt: resetState ? null : item?.completedAt || null,
      completedBy: resetState ? null : item?.completedBy || null,
      notes: resetState ? "" : item?.notes || "",
      children,
    });
  });

const normalizeStage = (stage, fallbackStage) => {
  const base = fallbackStage || createStage({
    id: stage?.id || createClientId("crm-stage"),
    label: stage?.label || "New Stage",
    color: stage?.color || "#64748b",
  });

  return {
    ...base,
    ...stage,
    id: stage?.id || base.id,
    label:
      stage?.label != null
        ? String(stage.label)
        : String(base.label || "New Stage"),
    color: String(stage?.color || base.color || "#64748b"),
    confirmsQuote: stage?.confirmsQuote === true || base.confirmsQuote === true,
    ordersMaterial: false,
    marksComplete: stage?.marksComplete === true || base.marksComplete === true,
    isCancelled: stage?.isCancelled === true || base.isCancelled === true,
    defaultChecklist: normalizeChecklistTemplate(
      stage?.defaultChecklist ?? base.defaultChecklist
    ),
  };
};

export const normalizeCrmPipelines = (pipelines = {}) => {
  const next = {};

  CRM_PIPELINE_TYPES.forEach((type) => {
    const defaults = DEFAULT_CRM_PIPELINES[type];
    const incoming = pipelines?.[type] || {};
    const incomingStages = Array.isArray(incoming.stages) ? incoming.stages : [];

    next[type] = {
      key: type,
      label:
        incoming.label != null ? String(incoming.label) : defaults.label,
      stages:
        incomingStages.length > 0
          ? incomingStages.map((stage) =>
              normalizeStage(
                stage,
                defaults.stages.find((defaultStage) => defaultStage.id === stage?.id)
              )
            )
          : defaults.stages.map((stage) => normalizeStage(stage, stage)),
    };

    if (!next[type].stages.some((stage) => stage.isCancelled)) {
      next[type].stages.push(
        normalizeStage(
          createStage({
            id: "cancelled",
            label: "Cancelled",
            color: "#ef4444",
            isCancelled: true,
          })
        )
      );
    }
  });

  return next;
};

export const getCrmPipeline = (crmPipelines, quoteType) =>
  normalizeCrmPipelines(crmPipelines)[
    quoteType === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE
  ];

export const getCrmStage = (crmPipelines, quoteType, stageId) => {
  const pipeline = getCrmPipeline(crmPipelines, quoteType);
  return (
    pipeline.stages.find((stage) => stage.id === stageId) ||
    pipeline.stages[0] ||
    null
  );
};

export const getDefaultCrmStageId = (crmPipelines, quoteType) =>
  getCrmPipeline(crmPipelines, quoteType)?.stages?.[0]?.id || "quote_given";

export const createChecklistFromStage = (stage) =>
  cloneChecklistTree(stage?.defaultChecklist || [], {
    idPrefix: "crm-check",
    preserveIds: false,
    resetState: true,
    useTemplateIds: true,
  });

export const normalizeDealChecklist = (items = [], stage = null) => {
  const normalized = normalizeChecklistTree(items);

  if (normalized.length > 0) return normalized;
  return createChecklistFromStage(stage);
};

export const getChecklistNodeProgress = (node) => {
  const children = Array.isArray(node?.children) ? node.children : [];
  const childProgresses = children.map((child) => getChecklistNodeProgress(child));
  const childProgress = childProgresses.reduce(
    (sum, current) => {
      return {
        total: sum.total + current.total,
        completed: sum.completed + current.completed,
      };
    },
    { total: 0, completed: 0 }
  );

  const nodeDone = children.length > 0
    ? childProgresses.every((child) => child.done === true)
    : node?.done === true;

  return {
    total: 1 + childProgress.total,
    completed: (nodeDone ? 1 : 0) + childProgress.completed,
    done: nodeDone,
    partial:
      !nodeDone &&
      childProgress.completed > 0 &&
      childProgress.completed < childProgress.total + 1,
  };
};

export const getChecklistProgress = (items = []) => {
  const checklistItems = normalizeChecklistTree(items);
  const summary = checklistItems.reduce(
    (sum, item) => {
      const progress = getChecklistNodeProgress(item);
      return {
        total: sum.total + progress.total,
        completed: sum.completed + progress.completed,
      };
    },
    { total: 0, completed: 0 }
  );

  return {
    total: summary.total,
    completed: summary.completed,
  };
};

export const getChecklistSummary = (leadLike = {}) => {
  const sourceItems = Array.isArray(leadLike?.checklistItems) && leadLike.checklistItems.length > 0
    ? leadLike.checklistItems
    : Array.isArray(leadLike?.checklist) && leadLike.checklist.length > 0
    ? leadLike.checklist
    : Array.isArray(leadLike?.stageChecklist) && leadLike.stageChecklist.length > 0
    ? leadLike.stageChecklist
    : [];
  const progress = getChecklistProgress(sourceItems);

  return {
    total: progress.total,
    completed: progress.completed,
    label:
      progress.total > 0
        ? `${progress.completed}/${progress.total} complete`
        : "No checklist",
  };
};

export const createStageDraft = (label = "New Stage") =>
  createStage({
    id: createClientId("crm-stage"),
    label,
    color: "#64748b",
  });

export const createChecklistTemplateDraft = (label = "New Checklist Item") => ({
  ...createChecklistNode(label, "crm-stage-task"),
  done: false,
  completed: false,
  checked: false,
});

export const createChecklistChildDraft = (
  label = "New Checklist Item",
  { templateMode = false, ...overrides } = {}
) =>
  createChecklistNode(
    label,
    templateMode ? "crm-stage-task" : "crm-check",
    overrides
  );

const updateChecklistTreeInternal = (items, targetId, updater) => {
  let changed = false;
  const next = (Array.isArray(items) ? items : []).map((item) => {
    let updatedItem = item;

    if (item.id === targetId) {
      updatedItem = updater(item);
      changed = true;
    }

    const nextChildren = updateChecklistTreeInternal(
      getChecklistChildren(updatedItem),
      targetId,
      updater
    );

    if (nextChildren.changed) {
      updatedItem = {
        ...updatedItem,
        children: nextChildren.items,
      };
      changed = true;
    }

    return updatedItem;
  });

  return { items: next, changed };
};

export const updateChecklistNodeById = (items = [], nodeId, updater) =>
  syncChecklistTreeCompletion(
    updateChecklistTreeInternal(items, nodeId, updater).items
  );

const deleteChecklistTreeInternal = (items, targetId) => {
  let changed = false;
  const next = (Array.isArray(items) ? items : [])
    .filter((item) => {
      if (item.id === targetId) {
        changed = true;
        return false;
      }
      return true;
    })
    .map((item) => {
      const nextChildren = deleteChecklistTreeInternal(
        getChecklistChildren(item),
        targetId
      );
      if (nextChildren.changed) {
        changed = true;
        return {
          ...item,
          children: nextChildren.items,
        };
      }
      return item;
    });

  return { items: next, changed };
};

export const deleteChecklistNodeById = (items = [], nodeId) =>
  syncChecklistTreeCompletion(deleteChecklistTreeInternal(items, nodeId).items);

const insertChecklistChildInternal = (items, parentId, childNode, depth = 1) => {
  let changed = false;
  const next = (Array.isArray(items) ? items : []).map((item) => {
    if (item.id === parentId) {
      if (depth >= CRM_CHECKLIST_MAX_DEPTH) {
        return item;
      }

      changed = true;
      return {
        ...item,
        children: [...(item.children || []), childNode],
      };
    }

    const nextChildren = insertChecklistChildInternal(
      getChecklistChildren(item),
      parentId,
      childNode,
      depth + 1
    );

    if (nextChildren.changed) {
      changed = true;
      return {
        ...item,
        children: nextChildren.items,
      };
    }

    return item;
  });

  return { items: next, changed };
};

export const insertChecklistChildNode = (items = [], parentId, childNode) =>
  syncChecklistTreeCompletion(
    insertChecklistChildInternal(items, parentId, childNode).items
  );

const moveChecklistNodeInternal = (items, nodeId, direction) => {
  const list = Array.isArray(items) ? [...items] : [];
  const index = list.findIndex((item) => item.id === nodeId);

  if (index >= 0) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= list.length) {
      return { items: list, changed: false };
    }

    const [moved] = list.splice(index, 1);
    list.splice(targetIndex, 0, moved);
    return { items: list, changed: true };
  }

  let changed = false;
  const next = list.map((item) => {
    const nextChildren = moveChecklistNodeInternal(
      getChecklistChildren(item),
      nodeId,
      direction
    );

    if (nextChildren.changed) {
      changed = true;
      return {
        ...item,
        children: nextChildren.items,
      };
    }

    return item;
  });

  return { items: next, changed };
};

export const moveChecklistNodeById = (items = [], nodeId, direction) =>
  syncChecklistTreeCompletion(
    moveChecklistNodeInternal(items, nodeId, direction).items
  );

export const toggleChecklistNodeById = (
  items = [],
  nodeId,
  checked,
  completion = {}
) => {
  const completedAt = checked ? completion.completedAt || new Date() : null;
  const completedBy = checked ? completion.completedBy || null : null;
  const updatedAt = completion.updatedAt || new Date();
  const toggleNode = (node) => ({
    ...node,
    done: checked,
    completed: checked,
    checked,
    completedAt,
    completedBy,
    updatedAt,
    children: getChecklistChildren(node).map(toggleNode),
  });

  return syncChecklistTreeCompletion(
    updateChecklistTreeInternal(items, nodeId, toggleNode).items
  );
};

export const getStageSelectOptions = (crmPipelines, quoteType, currentStageId) => {
  const pipeline = getCrmPipeline(crmPipelines, quoteType);
  const stages = [...pipeline.stages];

  if (currentStageId && !stages.some((stage) => stage.id === currentStageId)) {
    stages.unshift(
      createStage({
        id: currentStageId,
        label: String(currentStageId || "Legacy Stage"),
        color: "#64748b",
      })
    );
  }

  return stages;
};

export const getStageFilterOptions = (crmPipelines, quoteTypeFilter = "all") => {
  const types =
    quoteTypeFilter === "all"
      ? CRM_PIPELINE_TYPES
      : [quoteTypeFilter === GENERAL_QUOTE_TYPE ? GENERAL_QUOTE_TYPE : CURTAIN_QUOTE_TYPE];

  return types.flatMap((type) =>
    getCrmPipeline(crmPipelines, type).stages.map((stage) => ({
      key: `${type}::${stage.id}`,
      quoteType: type,
      stageId: stage.id,
      label: `${getQuoteTypeLabel(type)} | ${stage.label}`,
      color: stage.color,
    }))
  );
};

export const normalizeCrmReminderConfigs = (
  reminderConfigs = {},
  {
    nextActionDate = null,
    installationDate = null,
  } = {}
) => {
  const safeConfigs =
    reminderConfigs && typeof reminderConfigs === "object"
      ? reminderConfigs
      : {};

  return {
    [CRM_REMINDER_TYPE_TASK_DUE]: normalizeReminderConfigEntry(
      CRM_REMINDER_TYPE_TASK_DUE,
      safeConfigs[CRM_REMINDER_TYPE_TASK_DUE],
      toDateInputString(nextActionDate)
    ),
    [CRM_REMINDER_TYPE_INSTALLATION]: normalizeReminderConfigEntry(
      CRM_REMINDER_TYPE_INSTALLATION,
      safeConfigs[CRM_REMINDER_TYPE_INSTALLATION],
      toDateInputString(installationDate)
    ),
  };
};

export const getCrmReminderTypeLabel = (type) =>
  type === CRM_REMINDER_TYPE_INSTALLATION
    ? "Installation Reminder"
    : "Task Due Reminder";

export const getCrmReminderRepeatMinutes = (repeatKey) =>
  CRM_REMINDER_REPEAT_OPTIONS.find((option) => option.key === repeatKey)?.minutes ||
  1440;

const normalizeCrmFollowUpRepeat = (value) => {
  const nextValue = String(value || "").trim();
  return CRM_FOLLOW_UP_REPEAT_OPTIONS.some((option) => option.key === nextValue)
    ? nextValue
    : "none";
};

const normalizeCrmFollowUpStatus = (value) =>
  value === CRM_FOLLOW_UP_STATUS_DONE
    ? CRM_FOLLOW_UP_STATUS_DONE
    : CRM_FOLLOW_UP_STATUS_PENDING;

const normalizeCrmFollowUpPriority = (value) => {
  const nextValue = String(value || "").trim().toLowerCase();
  return CRM_FOLLOW_UP_PRIORITY_OPTIONS.some((option) => option.key === nextValue)
    ? nextValue
    : CRM_FOLLOW_UP_PRIORITY_MEDIUM;
};

const normalizeCrmFollowUpActor = (value = {}) => {
  if (typeof value === "string") {
    return {
      uid: "",
      name: value,
      email: "",
    };
  }

  return {
    uid: String(value?.uid || value?.completedByUid || value?.createdByUid || "").trim(),
    name: String(value?.name || value?.completedByName || value?.createdByName || "").trim(),
    email: String(value?.email || value?.completedByEmail || value?.createdByEmail || "").trim(),
  };
};

const sortCrmFollowUps = (followUps = []) =>
  [...followUps].sort((a, b) => {
    if (a.status !== b.status) {
      return a.status === CRM_FOLLOW_UP_STATUS_PENDING ? -1 : 1;
    }

    const aOrder = Number.isFinite(Number(a.order))
      ? Number(a.order)
      : Number.MAX_SAFE_INTEGER;
    const bOrder = Number.isFinite(Number(b.order))
      ? Number(b.order)
      : Number.MAX_SAFE_INTEGER;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    const aDue = new Date(
      `${a.dueDate || "9999-12-31"}T${a.dueTime || DEFAULT_CRM_REMINDER_TIME}:00`
    ).getTime();
    const bDue = new Date(
      `${b.dueDate || "9999-12-31"}T${b.dueTime || DEFAULT_CRM_REMINDER_TIME}:00`
    ).getTime();

    if (aDue !== bDue) {
      return aDue - bDue;
    }

    const aUpdated = new Date(a.updatedAt || a.createdAt || 0).getTime() || 0;
    const bUpdated = new Date(b.updatedAt || b.createdAt || 0).getTime() || 0;
    return bUpdated - aUpdated;
  });

export const normalizeCrmFollowUps = (
  followUps = [],
  {
    nextAction = "",
    nextActionDate = null,
    reminderConfig = null,
  } = {}
) => {
  const normalizedItems = (Array.isArray(followUps) ? followUps : [])
    .filter(Boolean)
    .map((item, index) => {
      const id =
        String(item?.id || item?.sourceTaskId || "").trim() ||
        `crm-follow-up-${index + 1}`;
      const order = Number.isFinite(Number(item?.order))
        ? Number(item.order)
        : index + 1;
      const dueDate = toDateInputString(item?.dueDate || item?.nextActionDate || "");
      const status = normalizeCrmFollowUpStatus(item?.status);
      const explicitReminderEnabled = normalizeBoolean(
        item?.reminderEnabled ?? item?.enabled
      );

      return {
        id,
        order,
        priority: normalizeCrmFollowUpPriority(item?.priority),
        reminderId:
          String(item?.reminderId || item?.reminderDocId || item?.reminder_id || "").trim() ||
          id,
        title: String(item?.title || item?.name || "").trim(),
        description: String(item?.description || item?.notes || "").trim(),
        dueDate,
        dueTime: normalizeReminderTime(
          item?.dueTime ?? item?.reminderTime ?? DEFAULT_CRM_REMINDER_TIME
        ),
        repeat: normalizeCrmFollowUpRepeat(item?.repeat || item?.repeatKey),
        reminderEnabled:
          explicitReminderEnabled !== undefined
            ? explicitReminderEnabled
            : status !== CRM_FOLLOW_UP_STATUS_DONE && Boolean(dueDate),
        status,
        createdAt: item?.createdAt || null,
        updatedAt: item?.updatedAt || null,
        createdBy: normalizeCrmFollowUpActor(item?.createdBy),
        completedAt: item?.completedAt || null,
        completedBy: normalizeCrmFollowUpActor(item?.completedBy),
        attachments: normalizeCrmAttachments(item?.attachments),
      };
    })
    .filter((item) => item.title || item.dueDate || item.attachments.length > 0);

  if (normalizedItems.length > 0) {
    return sortCrmFollowUps(normalizedItems);
  }

  const legacyTitle = String(nextAction || "").trim();
  const legacyDueDate = toDateInputString(nextActionDate);

  if (!legacyTitle && !legacyDueDate) {
    return [];
  }

  return [
    {
      id: "legacy-follow-up",
      order: 1,
      priority: CRM_FOLLOW_UP_PRIORITY_MEDIUM,
      reminderId: "legacy-follow-up",
      title: legacyTitle || "Follow-up",
      description: "",
      dueDate: legacyDueDate,
      dueTime: normalizeReminderTime(reminderConfig?.reminderTime),
      repeat: normalizeCrmFollowUpRepeat(reminderConfig?.repeatKey),
      reminderEnabled:
        normalizeBoolean(reminderConfig?.reminderEnabled ?? reminderConfig?.enabled) ??
        Boolean(legacyDueDate),
      status:
        reminderConfig?.status === CRM_REMINDER_STATUS_DONE
          ? CRM_FOLLOW_UP_STATUS_DONE
          : CRM_FOLLOW_UP_STATUS_PENDING,
      createdAt: null,
      updatedAt: null,
      createdBy: normalizeCrmFollowUpActor({}),
      completedAt: reminderConfig?.completedAt || null,
      completedBy: normalizeCrmFollowUpActor({}),
      attachments: [],
    },
  ];
};

const toReminderDateTimeMillis = (dateValue = "", timeValue = "") => {
  const date = toDateInputString(dateValue);
  if (!date) return 0;
  const time = normalizeReminderTime(timeValue);
  const millis = new Date(`${date}T${time}:00`).getTime();
  return Number.isNaN(millis) ? 0 : millis;
};

const formatFollowUpDateTimeLabel = (followUp = {}) => {
  const millis = toReminderDateTimeMillis(followUp.dueDate, followUp.dueTime);
  if (!millis) return "";

  return new Date(millis).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const getFollowUpSummary = (leadLike = {}, { nowMillis = Date.now() } = {}) => {
  const sourceFollowUps = Array.isArray(leadLike)
    ? leadLike
    : Array.isArray(leadLike?.followUps)
    ? leadLike.followUps
    : Array.isArray(leadLike?.crm?.followUps)
    ? leadLike.crm.followUps
    : [];
  const normalizedFollowUps = normalizeCrmFollowUps(sourceFollowUps, {
    nextAction: leadLike?.nextAction || leadLike?.crm?.nextAction || "",
    nextActionDate: leadLike?.nextActionDate || leadLike?.crm?.nextActionDate || null,
    reminderConfig:
      leadLike?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
      leadLike?.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_TASK_DUE] ||
      null,
  });
  const isDone = (followUp) =>
    followUp?.status === CRM_FOLLOW_UP_STATUS_DONE || Boolean(followUp?.completedAt);
  const pending = normalizedFollowUps.filter((followUp) => !isDone(followUp));
  const completed = normalizedFollowUps.length - pending.length;
  const overdue = pending.filter((followUp) => {
    const dueMillis = toReminderDateTimeMillis(followUp.dueDate, followUp.dueTime);
    return dueMillis > 0 && dueMillis < nowMillis;
  });
  const nextFollowUp =
    [...pending].sort((a, b) => {
      const aDue = toReminderDateTimeMillis(a.dueDate, a.dueTime) || Number.MAX_SAFE_INTEGER;
      const bDue = toReminderDateTimeMillis(b.dueDate, b.dueTime) || Number.MAX_SAFE_INTEGER;
      return aDue - bDue;
    })[0] || null;
  const nextFollowUpDateLabel = nextFollowUp ? formatFollowUpDateTimeLabel(nextFollowUp) : "";
  const nextFollowUpLabel = nextFollowUp
    ? `${nextFollowUp.title || "Follow-up"}${nextFollowUpDateLabel ? ` - ${nextFollowUpDateLabel}` : ""}`
    : "No follow-up set";

  return {
    total: normalizedFollowUps.length,
    pending: pending.length,
    completed,
    overdue: overdue.length,
    nextFollowUp,
    nextFollowUpLabel,
    label: `${pending.length} pending${overdue.length ? ` • ${overdue.length} overdue` : ""}`,
  };
};

export const getInstallationSummary = (leadLike = {}) => {
  const installationDate =
    toDateInputString(
      leadLike?.installationDate ||
        leadLike?.crm?.installationDate ||
        leadLike?.scheduledInstallationDate ||
        leadLike?.siteInstallDate ||
        leadLike?.installDate ||
        ""
    ) || "";
  const rawReminder =
    leadLike?.installationReminder ||
    leadLike?.crm?.installationReminder ||
    leadLike?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
    leadLike?.crm?.reminderConfigs?.[CRM_REMINDER_TYPE_INSTALLATION] ||
    leadLike?.reminderConfig ||
    leadLike?.crm?.reminderConfig ||
    {};
  const reminder = normalizeReminderConfigEntry(
    CRM_REMINDER_TYPE_INSTALLATION,
    rawReminder,
    installationDate
  );

  return {
    installationDate,
    installationLabel: installationDate || "-",
    reminderEnabled: reminder.enabled === true || reminder.reminderEnabled === true,
    reminderLabel:
      reminder.enabled === true || reminder.reminderEnabled === true
        ? "Reminder on"
        : "Reminder off",
  };
};

export const getPrimaryPendingCrmFollowUp = (followUps = []) =>
  sortCrmFollowUps(normalizeCrmFollowUps(followUps)).find(
    (item) => item.status !== CRM_FOLLOW_UP_STATUS_DONE
  ) || sortCrmFollowUps(normalizeCrmFollowUps(followUps))[0] || null;

export const getCrmFollowUpRepeatMinutes = (repeatKey) =>
  CRM_FOLLOW_UP_REPEAT_OPTIONS.find((option) => option.key === repeatKey)?.minutes ??
  0;

export const normalizeCrmAttachments = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .filter(Boolean)
    .map((attachment, index) => ({
      id: String(
        attachment?.id ||
          attachment?.storagePath ||
          attachment?.url ||
          attachment?.filename ||
          `crm-attachment-${index}`
      ),
      name: String(
        attachment?.name ||
          attachment?.filename ||
          attachment?.fileName ||
          "Attachment"
      ),
      filename: String(
        attachment?.filename ||
          attachment?.fileName ||
          attachment?.name ||
          "attachment"
      ),
      url: String(attachment?.url || attachment?.downloadUrl || ""),
      downloadURL: String(attachment?.downloadURL || attachment?.downloadUrl || attachment?.url || ""),
      thumbnailDownloadURL: String(
        attachment?.thumbnailDownloadURL ||
          attachment?.thumbnailUrl ||
          attachment?.thumbnailURL ||
          ""
      ),
      thumbnailFullPath: String(
        attachment?.thumbnailFullPath || attachment?.thumbnailStoragePath || ""
      ),
      storagePath: String(attachment?.storagePath || attachment?.path || ""),
      path: String(attachment?.path || attachment?.storagePath || ""),
      fullPath: String(attachment?.fullPath || attachment?.storagePath || attachment?.path || ""),
      fileId: String(attachment?.fileId || ""),
      imageId: String(attachment?.imageId || attachment?.id || ""),
      contentType: String(attachment?.contentType || attachment?.type || ""),
      sizeBytes: Number(attachment?.sizeBytes || attachment?.size || 0),
      thumbnailSizeBytes: Number(attachment?.thumbnailSizeBytes || 0),
      originalSizeBytes: Number(attachment?.originalSizeBytes || 0),
      optimizedSizeBytes: Number(attachment?.optimizedSizeBytes || 0),
      compressionRatio: Number(attachment?.compressionRatio || 0),
      width: Number(attachment?.width || 0),
      height: Number(attachment?.height || 0),
      originalWidth: Number(attachment?.originalWidth || 0),
      originalHeight: Number(attachment?.originalHeight || 0),
      uploadStatus: String(attachment?.uploadStatus || ""),
      compressionStatus: String(attachment?.compressionStatus || ""),
      thumbnailStatus: String(attachment?.thumbnailStatus || ""),
      ownerType: String(attachment?.ownerType || ""),
      workspaceUid: String(attachment?.workspaceUid || ""),
      leadId: String(attachment?.leadId || ""),
      ownerId: String(attachment?.ownerId || ""),
      removedFromRecord: attachment?.removedFromRecord === true,
      archived: attachment?.archived === true,
      uploadedAt: attachment?.uploadedAt || null,
      uploadedByUid: String(attachment?.uploadedByUid || ""),
      uploadedByName: String(
        attachment?.uploadedByName ||
          attachment?.uploadedBy ||
          ""
      ),
    }))
    .sort((a, b) => {
      const aTime = new Date(a.uploadedAt || 0).getTime() || 0;
      const bTime = new Date(b.uploadedAt || 0).getTime() || 0;
      return bTime - aTime;
    });

export const buildCrmContactFromQuote = (quote, quoteType, generalQuote = null) => {
  if (quoteType === GENERAL_QUOTE_TYPE) {
    const quoteFor = generalQuote?.quoteFor || quote?.generalQuote?.quoteFor || {};
    return {
      name: quoteFor.company || quoteFor.name || quote?.customerName || "",
      phone: quoteFor.phone || "",
      whatsapp: quoteFor.phone || "",
      email: quoteFor.email || "",
      address: quoteFor.address || "",
      source: "Quotation",
      notes: "",
    };
  }

  return {
    name: quote?.customerName || "",
    phone: "",
    whatsapp: "",
    email: "",
    address: "",
    source: "Quotation",
    notes: "",
  };
};
